import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, Image, ImageBackground, StyleSheet, TextInput, TouchableOpacity } from 'react-native';
import { resolveSkinChannelInfo, type SkinChannelInfo } from '../../services/skin/resolveSkinChannelInfo';
import { PHI_FIELD, getPhiValue } from '../../types/api/patinfo';
import type { PatinfoPhiDto } from '../../types/api/patinfo';

/** Respondent-entered PHI, keyed by PHI_FIELD name (always includes fname/lname). */
export type PhiCollectionFormValues = Record<string, string>;

interface PhiCollectionFormProps {
  /** SecureLoginResponseDto.dName — the assessment name shown in the card header. */
  dName: string;
  /** SecureLoginResponseDto.phi — determines which fields beyond First/Last Name to collect, and any pre-filled values. */
  phiFields: PatinfoPhiDto[];
  skinType?: string;
  storage?: any;
  onSubmit: (values: PhiCollectionFormValues) => void;
}

// Same header proportions as AdminView's question-page header, so this page looks identical
// to the assessment page it precedes. Duplicated rather than shared — see AdminView.tsx's own
// header block for the equivalent logic; kept independent since the two views evolve separately.
const HEADER_HEIGHT = 90;
const HEADER_LOGO_HEIGHT = 65;

// Fields that already get their own fixed First/Last Name rows, or that are SRA-side send
// configuration rather than something a respondent would fill in themselves.
const NON_FORM_FIELDS = new Set<string>([
  PHI_FIELD.FNAME,
  PHI_FIELD.LNAME,
  'patcode',
  PHI_FIELD.HOWCONTACT,
  PHI_FIELD.COPYPASTE,
  PHI_FIELD.LOCALSRA,
]);

const FIELD_LABELS: Record<string, string> = {
  [PHI_FIELD.DOB]: 'Date of Birth',
  [PHI_FIELD.GENDER]: 'Gender',
  [PHI_FIELD.EMAIL]: 'Email',
  [PHI_FIELD.PHONE]: 'Phone',
  [PHI_FIELD.ADDRESS]: 'Address',
  [PHI_FIELD.PATID]: 'Patient ID',
  [PHI_FIELD.PATIENTID]: 'Patient ID',
};

function humanizeFieldName(name: string): string {
  return FIELD_LABELS[name.toLowerCase()] || (name.charAt(0).toUpperCase() + name.slice(1));
}

export const PhiCollectionForm: React.FC<PhiCollectionFormProps> = ({ dName, phiFields, skinType, storage, onSubmit }) => {
  // Skin branding (logo/background/bg color) for the page header — same lookup AdminView's
  // question-page header uses.
  const [skinChannelInfo, setSkinChannelInfo] = useState<SkinChannelInfo | null>(null);
  useEffect(() => {
    if (!storage || !skinType) {
      return;
    }
    let cancelled = false;
    void resolveSkinChannelInfo(storage, skinType).then((resolved) => {
      if (!cancelled) {
        setSkinChannelInfo(resolved);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [storage, skinType]);

  // RN Image needs an explicit numeric width — measure the logo's natural aspect ratio and
  // scale width to match the fixed header logo height (same approach as AdminView/ProGroupsMainMenu).
  const [skinLogoWidth, setSkinLogoWidth] = useState<number | null>(null);
  useEffect(() => {
    const logoSrc = skinChannelInfo?.logoSrc;
    if (!logoSrc) {
      setSkinLogoWidth(null);
      return;
    }
    let cancelled = false;
    Image.getSize(
      logoSrc,
      (naturalWidth, naturalHeight) => {
        if (!cancelled && naturalHeight > 0) {
          setSkinLogoWidth((naturalWidth / naturalHeight) * HEADER_LOGO_HEIGHT);
        }
      },
      (error) => {
        console.warn('PhiCollectionForm: failed to measure skin header logo size', error);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [skinChannelInfo?.logoSrc]);

  // Fields the link's launch configuration wants collected, beyond the always-shown First/Last
  // Name rows — de-duplicated, in the order they appear in resolved.phi.
  const otherFields = useMemo(() => {
    const seen = new Set<string>();
    return (phiFields ?? []).filter((entry) => {
      const name = (entry.name || '').toLowerCase();
      if (!name || NON_FORM_FIELDS.has(name) || seen.has(name)) {
        return false;
      }
      seen.add(name);
      return true;
    });
  }, [phiFields]);

  const [values, setValues] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {
      [PHI_FIELD.FNAME]: getPhiValue(phiFields ?? [], PHI_FIELD.FNAME),
      [PHI_FIELD.LNAME]: getPhiValue(phiFields ?? [], PHI_FIELD.LNAME),
    };
    otherFields.forEach((field) => {
      initial[field.name] = field.value ?? '';
    });
    return initial;
  });
  const [fnameError, setFnameError] = useState<string | null>(null);

  const setFieldValue = (name: string, value: string) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = () => {
    const fname = (values[PHI_FIELD.FNAME] ?? '').trim();
    if (!fname) {
      setFnameError('First name is required');
      return;
    }
    setFnameError(null);
    onSubmit({ ...values, [PHI_FIELD.FNAME]: fname, [PHI_FIELD.LNAME]: (values[PHI_FIELD.LNAME] ?? '').trim() });
  };

  const skinLogo = skinChannelInfo?.logoSrc ? (
    <Image
      source={{ uri: skinChannelInfo.logoSrc }}
      style={[styles.skinHeaderLogo, skinLogoWidth ? { width: skinLogoWidth } : null]}
      resizeMode="contain"
    />
  ) : null;

  return (
    <View style={styles.page}>
      <View
        style={[
          styles.skinHeaderShell,
          skinChannelInfo?.backgroundSrc
            ? { backgroundColor: 'transparent' }
            : skinChannelInfo?.skinBgColor
            ? { backgroundColor: skinChannelInfo.skinBgColor }
            : null,
        ]}
      >
        {skinChannelInfo?.backgroundSrc ? (
          <ImageBackground
            source={{ uri: skinChannelInfo.backgroundSrc }}
            style={styles.skinHeaderBackgroundImage}
            resizeMode="stretch"
          >
            {skinLogo}
          </ImageBackground>
        ) : (
          skinLogo
        )}
      </View>

      <View style={styles.questionCard}>
        <Text style={styles.dNameText}>{dName}</Text>

        <View style={styles.spacer} />
        <Text style={styles.introLabel}>Please enter your information to start the assessment.</Text>

        <View style={styles.fieldsBlock}>
          <PhiRow
            label="First Name*"
            value={values[PHI_FIELD.FNAME] ?? ''}
            onChangeText={(t) => setFieldValue(PHI_FIELD.FNAME, t)}
            error={fnameError}
            autoCapitalize="words"
          />
          <PhiRow
            label="Last Name"
            value={values[PHI_FIELD.LNAME] ?? ''}
            onChangeText={(t) => setFieldValue(PHI_FIELD.LNAME, t)}
            autoCapitalize="words"
          />
          {otherFields.map((field) => (
            <PhiRow
              key={field.name}
              label={humanizeFieldName(field.name)}
              value={values[field.name] ?? ''}
              onChangeText={(t) => setFieldValue(field.name, t)}
            />
          ))}
          <View style={styles.footerRow}>
            <View style={styles.phiLabelCol} />
            <Text style={styles.requiredNote}>* required</Text>
          </View>
        </View>

        <View style={styles.startButtonRow}>
          <TouchableOpacity
            style={styles.startButton}
            onPress={handleSubmit}
          >
            <Text style={styles.startButtonText}>Start</Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

interface PhiRowProps {
  label: string;
  value: string;
  onChangeText: (text: string) => void;
  error?: string | null;
  autoCapitalize?: 'none' | 'words';
}

const PhiRow: React.FC<PhiRowProps> = ({ label, value, onChangeText, error, autoCapitalize = 'none' }) => (
  <View style={styles.phiRow}>
    <View style={styles.phiLabelCol}>
      <Text style={styles.phiLabel}>{label}</Text>
    </View>
    <View>
      <TextInput
        style={[styles.phiInput, error ? styles.phiInputError : null]}
        value={value}
        onChangeText={onChangeText}
        autoCapitalize={autoCapitalize}
      />
      {error && <Text style={styles.phiErrorText}>{error}</Text>}
    </View>
  </View>
);

const styles = StyleSheet.create({
  page: {
    flex: 1,
    backgroundColor: '#ffffff',
  },
  skinHeaderShell: {
    width: '100%',
    height: HEADER_HEIGHT,
    backgroundColor: 'transparent',
  },
  skinHeaderBackgroundImage: {
    width: '100%',
    height: HEADER_HEIGHT,
    justifyContent: 'center',
    paddingHorizontal: 24,
  },
  skinHeaderLogo: {
    height: HEADER_LOGO_HEIGHT,
    width: HEADER_LOGO_HEIGHT,
    marginLeft: 24,
  },
  questionCard: {
    width: '100%',
    maxWidth: 800,
    alignSelf: 'center',
    marginTop: 16,
    marginBottom: 16,
    borderRadius: 20,
    backgroundColor: '#f7f7f7',
    borderWidth: 1,
    borderColor: '#f7f7f7',
    paddingLeft: 30,
    paddingRight: 20,
    paddingTop: 30,
    shadowColor: '#dbdbdb',
    shadowOffset: { width: 1, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 0,
    elevation: 2,
  },
  dNameText: {
    fontFamily: 'Arial',
    fontSize: 18,
    fontWeight: '600',
    color: '#1f2937',
    textAlign: 'left',
  },
  spacer: {
    height: 30,
  },
  introLabel: {
    fontFamily: 'Arial',
    fontSize: 17,
    color: '#374151',
  },
  fieldsBlock: {
    alignSelf: 'flex-start',
    marginTop: 16,
  },
  phiRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  phiLabelCol: {
    width: 100,
    paddingTop: 8,
    marginRight: 12,
  },
  phiLabel: {
    fontFamily: 'Arial',
    fontSize: 17,
    color: '#1f2937',
  },
  phiInput: {
    width: 150,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 2,
    paddingVertical: 6,
    paddingHorizontal: 8,
    fontFamily: 'Arial',
    fontSize: 17,
    backgroundColor: '#ffffff',
    color: '#1f2937',
  },
  phiInputError: {
    borderColor: '#e74c3c',
  },
  phiErrorText: {
    fontFamily: 'Arial',
    fontSize: 15,
    color: '#e74c3c',
    marginTop: 4,
  },
  footerRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  requiredNote: {
    fontFamily: 'Arial',
    fontSize: 15,
    color: '#6b7280',
  },
  startButtonRow: {
    alignItems: 'center',
    marginTop: 12,
    paddingTop: 24,
    paddingBottom: 40,
  },
  startButton: {
    minWidth: 200,
    height: 55,
    paddingHorizontal: 15,
    borderRadius: 10,
    borderWidth: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#e5e5e5',
    shadowColor: '#b7b7b7',
    shadowOffset: { width: 1, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 0,
    elevation: 3,
  },
  startButtonText: {
    fontFamily: 'Arial',
    fontSize: 17,
    fontWeight: '500',
    color: '#333333',
  },
});
